import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/queryClient";
import { adminSetAppConfig, adminSetBillingConfig } from "@/lib/callables";
import type { AppConfig, BillingConfig } from "@/types";
import { fetchDocument } from "./use-firestore";

/**
 * `config/app` guarda as versões mínimas que os dois aplicativos leem no
 * arranque. Subir uma delas acima da versão publicada nas lojas mostra a tela
 * de atualização para toda a base: é a escrita de maior alcance do painel.
 */
export function useAppConfig() {
  return useQuery({
    queryKey: queryKeys.configApp(),
    queryFn: () => fetchDocument<AppConfig & { id: string }>("config", "app"),
  });
}

/**
 * `config/billing.platformFeePercentage` é a taxa da plataforma. Ela é lida
 * pelo cron diário de faturamento do backend.
 */
export function useBillingConfig() {
  return useQuery({
    queryKey: queryKeys.configBilling(),
    queryFn: () => fetchDocument<BillingConfig & { id: string }>("config", "billing"),
  });
}

export function useSetAppConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { clientMinVersion?: string; sellerMinVersion?: string }) =>
      adminSetAppConfig(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.configApp() }),
  });
}

export function useSetBillingConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { platformFeePercentage: number }) => adminSetBillingConfig(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.configBilling() }),
  });
}
